import { type IChatSettings } from "./chat";

export interface IProject {
  uuid: string;
  title: string;
  // NOTE: Whether the current user's role in this project grants
  // api_key:write. Surfaced by the projects endpoint so the UI can gate the
  // "create API key" action per-project (the create endpoint enforces this
  // permission against the selected project). Optional so existing fixtures
  // and consumers that omit it stay valid; absent is treated as writable
  // (fail-open — the backend remains the real gate).
  canWriteApiKeys?: boolean;
}

export interface IOrganizationProject {
  uuid: string;
  name: string;
  projects: IProject[];
  // NOTE: Member-visible (not owner-gated) flag = the org has a search
  // embedding model configured. Used to surface cross-chat search on
  // on-premise builds, where it is otherwise hidden. Optional so existing
  // fixtures that omit it stay valid; absent is treated as false.
  hasSearchEmbeddingModel?: boolean;
}

export interface IParsedProject {
  uuid: string;
  title: string;
  createdAt: string;
  memberCount: number;
  permissions: {
    canReadProject: boolean;
    canUpdateProject: boolean;
    canDeleteProject: boolean;
    canInviteMembersToProject: boolean;
    canUpdateProjectMemberRole: boolean;
    canRemoveProjectMember: boolean;
    canReadProjectRole: boolean;
    canWriteProjectRole: boolean;
    canDeleteProjectRole: boolean;
    canReadApiKeys: boolean;
    canWriteApiKeys: boolean;
    canDeleteApiKeys: boolean;
    canReadAnalytics: boolean;
    canReadAllMembersAnalytics: boolean;
    canReadUsageLimits: boolean;
    // NOTE: NSAI-6213 — the SIEM export settings levels. The server already
    // folds the ladder in, so canReadSiemConfig is true for a write or delete
    // holder too; a client never has to re-derive that.
    canReadSiemConfig: boolean;
    canWriteSiemConfig: boolean;
    canDeleteSiemConfig: boolean;
    // NOTE: NSAI-6369 — the organization memory-policy levels. As with the SIEM
    // flags above, the server folds the ladder in: canReadMemoryPolicy is true
    // for a write holder too, so a client never re-derives it.
    canReadMemoryPolicy: boolean;
    canWriteMemoryPolicy: boolean;
    canReadExternalApiLimits: boolean;
    canWriteExternalApiLimits: boolean;
    canDeleteExternalApiLimits: boolean;
  };
}

export interface IProjectList {
  organizationProjects: IOrganizationProject[];
  personalProject: {
    uuid: string;
    title: string;
    // NOTE: Always true in practice (the personal project uses the wildcard
    // Admin role); optional to keep existing fixtures valid.
    canWriteApiKeys?: boolean;
  } | null;
}

export interface IProjectMember {
  uuid: string;
  userUuid: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  // NOTE: NSAI-7736 — the IdP login name of a member with no email.
  username?: string | null;
}

export interface ICreateProjectBody {
  title: string;
  organizationUuid: string;
}

export interface IEditProjectBody {
  title?: string;
}

export interface IEditProjectParams {
  uuid: string;
  data: IEditProjectBody;
}

// NOTE: NSAI-7736 — who a project invite is addressed to: an email, or a
// login name of the organization's IdP for a member with no address.
export enum EInviteRecipientType {
  EMAIL = "EMAIL",
  USERNAME = "USERNAME",
}

// NOTE: NSAI-7736 — the project invite form's values (web dialog + mobile
// modal), declared here rather than inferred from the schema so the payload
// helper does not pull the zod schema (and its typed i18n keys) into every
// package that imports the utils barrel.
export interface IInviteProjectMemberFormValues {
  recipientType: EInviteRecipientType;
  email: string;
  username: string;
  roleUuid: string;
  projectUuid: string;
}

// NOTE: NSAI-7736 — exactly one of `email` / `username`. A username invite is
// offered only when the organization identifies SSO members by NameID or by an
// attribute (IOrganization.samlUserIdentifier), which only a server that knows
// the field can report.
export interface IInviteToProjectBody {
  roleUuid: string;
  email?: string;
  username?: string;
}

export interface IInviteToProjectParams {
  uuid: string;
  data: IInviteToProjectBody;
}

export interface IUpdateMemberRoleBody {
  roleUuid: string;
}

export interface IRemoveProjectMemberParams {
  uuid: string;
  memberUuid: string;
}

export interface IUpdateProjectMemberRoleParams {
  uuid: string;
  memberUuid: string;
  data: IUpdateMemberRoleBody;
}

export interface ICreateProjectRoleBody {
  name: string;
  permissions: string[];
  description?: string | null;
}

export interface IUpdateProjectRoleBody {
  name?: string;
  permissions?: string[];
  description?: string | null;
}

export interface IProjectRole {
  uuid: string;
  name: string;
  description: string | null;
  permissions: Record<string, string[]>;
}

export type TProjectPermissionsMap = Record<string, string[]>;

export interface ICreateProjectRoleParams {
  uuid: string;
  data: ICreateProjectRoleBody;
}

export interface IUpdateProjectRoleParams {
  uuid: string;
  roleUuid: string;
  data: IUpdateProjectRoleBody;
}

export interface IDeleteProjectRoleParams {
  uuid: string;
  roleUuid: string;
}

export interface IUpdateChatSettingsParams {
  organizationUuid: string;
  body: IChatSettings;
}
